import React, { useEffect, useRef, useState } from "react";
import XLSX from "xlsx";
import SubCard from "../../ui-component/cards/SubCard";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import ApiInstance from "../../Services/ApiInstance";
import axios from "axios";
import { decryptData, encryptData, getDecryptDataFromLocal, setencryptDataInLocal } from "../../utils/encrypt";

// Bulk create (sheet upload / paste) — only these three users.
const BULK_SELLER_CREATE_PHONES = ["7838688729", "8826345311", "9719836187"];
const BULK_SELLER_CREATE_MAX = 50;
const INFLUENCER_DEFAULT_COD_LIMIT = 1000;

// Influencers get a fixed COD limit (server sets it) — shown here, not editable.
const withInfluencer = (r, value) => ({ ...r, isInfluencer: value });

const normalizePhone = (v) => {
  let p = String(v ?? "").replace(/\D/g, "");
  if (p.length === 12 && p.startsWith("91")) p = p.slice(2);
  return p;
};
const isValidPhone = (p) => /^[6-9]\d{9}$/.test(p);

// A row is [a, b] in any order — whichever cell looks like a phone number is the
// phone, the other is the name. Header rows ("Name", "Phone") fall out naturally.
const cellsToSeller = (cells) => {
  const vals = cells.map((c) => String(c ?? "").trim()).filter(Boolean);
  if (!vals.length) return null;
  const phoneIdx = vals.findIndex((v) => isValidPhone(normalizePhone(v)));
  if (phoneIdx === -1) {
    // keep obviously-broken rows visible so they can be fixed in the table
    if (vals.length >= 2 && /\d{5,}/.test(vals.join(""))) {
      const idx = vals.findIndex((v) => /\d{5,}/.test(v));
      return { name: vals.filter((_, i) => i !== idx).join(" "), phoneNumber: normalizePhone(vals[idx]) };
    }
    return null;
  }
  return {
    name: vals.filter((_, i) => i !== phoneIdx).join(" "),
    phoneNumber: normalizePhone(vals[phoneIdx]),
  };
};

let rowSeq = 0;
const newRow = (s) => ({
  id: ++rowSeq,
  name: s.name || "",
  phoneNumber: s.phoneNumber || "",
  allowBlow1korder: false,
  isInfluencer: false,
  status: "",
  message: "",
});

const rowError = (row, rows) => {
  if (!row.name.trim()) return "Name is required";
  if (!isValidPhone(normalizePhone(row.phoneNumber))) return "Invalid phone number";
  const p = normalizePhone(row.phoneNumber);
  if (rows.filter((r) => normalizePhone(r.phoneNumber) === p).length > 1) return "Duplicate in this list";
  return "";
};

const STATUS_COLOR = { created: "#2e7d32", ok: "#1565c0", error: "#c62828" };

function BulkSellerRegister() {
  const [rows, setRows] = useState([]);
  const [pasteText, setPasteText] = useState("");
  const [confirmStep, setConfirmStep] = useState(0); // 0 closed, 1 first, 2 second
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const pendingRows = rows.filter((r) => r.status !== "created");
  const allChecked = (field) => pendingRows.length > 0 && pendingRows.every((r) => r[field]);
  const someChecked = (field) => pendingRows.some((r) => r[field]);
  const hasLocalErrors = pendingRows.some((r) => rowError(r, pendingRows));
  // Create is only allowed once the server check passed for EVERY row (valid phone,
  // not an existing seller, WA rule). Editing name/phone clears the row's status,
  // which re-triggers the auto check below.
  const allRowsReady = pendingRows.length > 0 && !hasLocalErrors && pendingRows.every((r) => r.status === "ok");
  const needsCheck = !hasLocalErrors && pendingRows.some((r) => !r.status);
  const checkKey = pendingRows.map((r) => `${r.id}:${r.name}:${r.phoneNumber}:${r.status}`).join("|");

  const addSellers = (parsed) => {
    if (!parsed.length) return alert("No valid name / phone number rows found");
    setRows((prev) => {
      const kept = prev.filter((r) => r.status !== "created");
      const room = BULK_SELLER_CREATE_MAX - kept.length;
      if (room <= 0) {
        alert(`Max ${BULK_SELLER_CREATE_MAX} sellers can be created at once`);
        return prev;
      }
      if (parsed.length > room) {
        alert(`Max ${BULK_SELLER_CREATE_MAX} sellers at once — only the first ${room} rows were added, ${parsed.length - room} ignored`);
      }
      return [...kept, ...parsed.slice(0, room).map(newRow)];
    });
  };

  const handlePaste = () => {
    const parsed = pasteText
      .split(/\r?\n/)
      .map((line) => cellsToSeller(line.split(/\t|,|;|\|/)))
      .filter(Boolean);
    addSellers(parsed);
    if (parsed.length) setPasteText("");
  };

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
        addSellers(data.map(cellsToSeller).filter(Boolean));
      } catch (err) {
        alert("Could not read the file");
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  // resetStatus=false for checkbox flags — they don't change whether a row is valid
  const downloadSample = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Name", "Phone Number"],
      ["Ramesh Kumar", "9876543210"],
      ["Sita Salon", "9123456789"],
    ]);
    ws["!cols"] = [{ wch: 24 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sellers");
    XLSX.writeFile(wb, "seller_register_sample.xlsx");
  };

  const updateRow = (id, patch, resetStatus = true) =>
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch, ...(resetStatus ? { status: "", message: "" } : {}) } : r))
    );

  // header checkbox — flips a boolean field for every not-yet-created row
  const setAll = (field, value) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.status === "created") return r;
        return field === "isInfluencer" ? withInfluencer(r, value) : { ...r, [field]: value };
      })
    );

  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  const callBulk = (dryRun) => {
    if (!pendingRows.length) return;
    const sent = pendingRows;
    setLoading(true);
    ApiInstance.post("/seller/createNewSellerBulk", {
      dryRun,
      sellers: sent.map((r) => ({
        name: r.name.trim(),
        phoneNumber: normalizePhone(r.phoneNumber),
        allowBlow1korder: !!r.allowBlow1korder,
        isInfluencer: !!r.isInfluencer,
      })),
    })
      .then((res) => {
        if (!res.data.success) return alert(res.data?.message || "Something went wrong");
        const { results = [], created, failed } = res.data.data || {};
        // results come back in the same order as the rows sent; skip a row whose
        // name/phone was edited while the request was in flight
        const byId = {};
        sent.forEach((r, i) => (byId[r.id] = { sent: r, result: results[i] }));
        setRows((prev) =>
          prev.map((r) => {
            const hit = byId[r.id];
            if (!hit || !hit.result) return r;
            if (hit.sent.name !== r.name || hit.sent.phoneNumber !== r.phoneNumber) return r;
            return { ...r, status: hit.result.status, message: hit.result.message };
          })
        );
        if (!dryRun) alert(`${created} seller(s) created, ${failed} failed`);
      })
      .catch((err) =>
        alert(err?.response?.status === 403 ? "Session expired — please log in again" : "Something went wrong")
      )
      .finally(() => setLoading(false));
  };

  // auto-run the server check (dry run) after rows are added/edited
  useEffect(() => {
    if (!needsCheck || loading) return undefined;
    const t = setTimeout(() => callBulk(true), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkKey, needsCheck, loading]);

  const startCreate = () => {
    if (!allRowsReady) return alert("Fix or remove the invalid / existing rows before creating");
    setConfirmStep(1);
  };

  const below1kCount = pendingRows.filter((r) => r.allowBlow1korder).length;
  const influencerCount = pendingRows.filter((r) => r.isInfluencer).length;

  return (
    <SubCard title="Bulk Seller Register" style={{ marginTop: 16 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            variant="outlined"
            label="Paste Name and Phone Number (one seller per line, e.g. copied from a sheet)"
            placeholder={"Ramesh Kumar\t9876543210\nSita Salon, 9123456789"}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Button variant="contained" color="primary" onClick={handlePaste} disabled={!pasteText.trim()}>
            Add Pasted Rows
          </Button>
          <Button variant="outlined" color="primary" onClick={() => fileRef.current && fileRef.current.click()}>
            Upload Excel / CSV
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleFile} />
          <Button size="small" onClick={downloadSample}>
            Download Sample Excel
          </Button>
          <Typography variant="caption" color="textSecondary">
            Sheet needs a Name column and a Phone Number column. Max {BULK_SELLER_CREATE_MAX} sellers at once.
          </Typography>
        </Grid>

        {rows.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
              {pendingRows.length} / {BULK_SELLER_CREATE_MAX} sellers to create
            </Typography>
            <TableContainer style={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Phone Number</TableCell>
                    {[
                      ["allowBlow1korder", "Allow Below 1K Order (all)"],
                      ["isInfluencer", "Is Influencer (all)"],
                    ].map(([field, label]) => (
                      <TableCell key={field}>
                        <Checkbox
                          size="small"
                          checked={allChecked(field)}
                          indeterminate={someChecked(field) && !allChecked(field)}
                          onChange={(e) => setAll(field, e.target.checked)}
                          disabled={!pendingRows.length}
                        />
                        {label}
                      </TableCell>
                    ))}
                    <TableCell>COD Limit (influencer only)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => {
                    const done = r.status === "created";
                    const localErr = done ? "" : rowError(r, pendingRows);
                    return (
                      <TableRow key={r.id}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="outlined"
                            value={r.name}
                            disabled={done}
                            error={!done && !r.name.trim()}
                            onChange={(e) => updateRow(r.id, { name: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="outlined"
                            type="tel"
                            value={r.phoneNumber}
                            disabled={done}
                            error={!done && !isValidPhone(normalizePhone(r.phoneNumber))}
                            onChange={(e) => updateRow(r.id, { phoneNumber: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            size="small"
                            checked={!!r.allowBlow1korder}
                            disabled={done}
                            onChange={(e) => updateRow(r.id, { allowBlow1korder: e.target.checked }, false)}
                          />
                          {r.allowBlow1korder ? "True" : "False"}
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            size="small"
                            checked={!!r.isInfluencer}
                            disabled={done}
                            onChange={(e) =>
                              setRows((prev) =>
                                prev.map((x) =>
                                  x.id === r.id ? withInfluencer(x, e.target.checked) : x
                                )
                              )
                            }
                          />
                          {r.isInfluencer ? "True" : "False"}
                        </TableCell>
                        <TableCell>
                          {r.isInfluencer ? `₹${INFLUENCER_DEFAULT_COD_LIMIT}` : "-"}
                        </TableCell>
                        <TableCell style={{ color: STATUS_COLOR[localErr ? "error" : r.status] }}>
                          {localErr || r.message}
                        </TableCell>
                        <TableCell>
                          {!done && (
                            <Button size="small" color="secondary" onClick={() => removeRow(r.id)}>
                              Remove
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Button variant="outlined" onClick={() => callBulk(true)} disabled={loading || !pendingRows.length || hasLocalErrors}>
                Check
              </Button>
              <Button variant="contained" color="primary" onClick={startCreate} disabled={loading || !allRowsReady}>
                {loading ? "Please wait..." : `Create ${pendingRows.length} Seller(s)`}
              </Button>
              <Button onClick={() => setRows([])} disabled={loading}>
                Clear
              </Button>
              {!allRowsReady && pendingRows.length > 0 && (
                <Typography variant="caption" style={{ alignSelf: "center", color: STATUS_COLOR.error }}>
                  {loading || needsCheck
                    ? "Checking numbers..."
                    : "Fix or remove the rows marked in red to enable Create"}
                </Typography>
              )}
            </div>
          </Grid>
        )}
      </Grid>

      <Dialog open={confirmStep === 1} onClose={() => setConfirmStep(0)}>
        <DialogTitle>Create Sellers?</DialogTitle>
        <DialogContent>
          Are you sure you want to create {pendingRows.length} seller(s)?
          <br />
          Allow Below 1K Order: True for {below1kCount}, False for {pendingRows.length - below1kCount}.
          <br />
          Is Influencer: True for {influencerCount}, False for {pendingRows.length - influencerCount}
          {influencerCount > 0 ? ` (COD limit ₹${INFLUENCER_DEFAULT_COD_LIMIT} for influencers)` : ""}.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStep(0)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={() => setConfirmStep(2)}>
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmStep === 2} onClose={() => setConfirmStep(0)}>
        <DialogTitle>Final Confirmation</DialogTitle>
        <DialogContent>
          This will create {pendingRows.length} seller(s) and cannot be undone. Are you really sure?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStep(0)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setConfirmStep(0);
              callBulk(false);
            }}
          >
            Yes, Create Sellers
          </Button>
        </DialogActions>
      </Dialog>
    </SubCard>
  );
}

// Fix a seller whose name is still the website/app placeholder "Dear Customer"
// (or blank) — orders can't be placed for them until it's set (Shailendra,
// 2026-10-14). Only such names are editable here; a real name stays as is.
const AI_PATH = process.env.REACT_APP_AI_PATH;
const isRealSellerName = (n) => {
  const v = String(n || "").trim();
  return v.length >= 2 && v.toLowerCase() !== "dear customer";
};

function UpdateSellerName() {
  const [phone, setPhone] = useState("");
  const [seller, setSeller] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'ok' | 'err', text }

  const fetchSeller = async (e) => {
    e && e.preventDefault();
    const p = normalizePhone(phone);
    setSeller(null); setName(""); setMsg(null);
    if (!isValidPhone(p)) return setMsg({ type: "err", text: "Enter a valid 10-digit phone number" });
    setBusy(true);
    try {
      const res = await axios.get(`${AI_PATH}/api/ai/orders/seller-by-phone/${p}`);
      if (!res.data?.found) return setMsg({ type: "err", text: `No seller found for ${p}` });
      setSeller(res.data.seller);
    } catch (err) {
      setMsg({ type: "err", text: "Could not fetch the seller" });
    } finally { setBusy(false); }
  };

  const saveName = async () => {
    if (!isRealSellerName(name)) return setMsg({ type: "err", text: 'Enter the real name (at least 2 letters, not "Dear Customer")' });
    setBusy(true); setMsg(null);
    try {
      const res = await axios.patch(`${AI_PATH}/api/ai/orders/seller/${seller._id}/profile`, { name: name.trim() });
      if (!res.data?.success) throw new Error(res.data?.error || "Could not update");
      setSeller((prev) => ({ ...prev, name: name.trim() }));
      setMsg({ type: "ok", text: `Name updated to "${name.trim()}" — orders can now be placed for this seller` });
    } catch (err) {
      setMsg({ type: "err", text: err.response?.data?.error || err.message || "Could not update" });
    } finally { setBusy(false); }
  };

  const editable = seller && !isRealSellerName(seller.name);

  return (
    <SubCard title="Update Seller Name (Dear Customer)" style={{ marginTop: 16 }}>
      <form onSubmit={fetchSeller}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField fullWidth variant="outlined" label="Seller Phone Number" type="tel"
              value={phone} onChange={(e) => { setPhone(e.target.value); setSeller(null); setMsg(null); }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="outlined" type="submit" disabled={busy}>Fetch Seller</Button>
          </Grid>
        </Grid>
      </form>

      {seller && (
        <div style={{ marginTop: 16 }}>
          <Typography variant="body2">
            <b>Current name:</b> {seller.name || "(blank)"} &nbsp;|&nbsp; <b>Phone:</b> {seller.phoneNumber}
            {seller.businessName ? <> &nbsp;|&nbsp; <b>Business:</b> {seller.businessName}</> : null}
          </Typography>
          {editable ? (
            <Grid container spacing={1} alignItems="center" style={{ marginTop: 8 }}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth variant="outlined" label="New Name *" value={name}
                  onChange={(e) => setName(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="contained" color="primary" onClick={saveName} disabled={busy}>
                  {busy ? "Saving..." : "Update Name"}
                </Button>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="caption" style={{ display: "block", marginTop: 6, color: "#2e7d32" }}>
              This seller already has a real name — nothing to update here.
            </Typography>
          )}
        </div>
      )}

      {msg && (
        <Typography variant="body2" style={{ marginTop: 10, color: msg.type === "ok" ? "#2e7d32" : "#c62828" }}>
          {msg.text}
        </Typography>
      )}
    </SubCard>
  );
}

function SalonUserRegister() {
  const canBulkCreate = BULK_SELLER_CREATE_PHONES.includes(String(getDecryptDataFromLocal("phoneNumber") || ""));
  const [sellerDetails, setSellerDetails] = useState({
    name: "",
    phoneNumber: "",
  });
  const sellerCreate = (e) => {
    e.preventDefault();
    console.log("sellerDetails", sellerDetails);
    if(sellerDetails.phoneNumber.length==10){
    // referedBy/refferdByAgenttype are no longer user-editable — every seller
    // created here is BULK, referred by Raj1641 (server enforces this too).
    ApiInstance.post("/seller/createNewSeller", {
      name: sellerDetails.name.trim(),
      phoneNumber: sellerDetails.phoneNumber.trim(),
    })
      .then((res) => {
        if (res.data.success) {
          alert(res.data?.message || "Seller created succesfully");
          window.location.reload();
        } else {
          alert(res.data?.message || "Something went wrong");
        }
      })
      .catch((error) => {
        alert("Something went wrong");
      });
}else{
    alert('Invalid Phone Number')
}
  };



  const checkFun = ()=>{
    let da = getDecryptDataFromLocal('testing')
    console.log('daaa',da)
  }
  const checkFun2 = ()=>{
    let da = setencryptDataInLocal('testing',{
      name:'ajsbh',
      phone:299299292
    })
    console.log('daaa--',da)
  }






  return (
    <>
    <SubCard>
      <form onSubmit={sellerCreate}>
        <Grid container xs={12} spacing={1}>
          <Grid item xs={12} md={4} gap={2}>
            <TextField
              id="outlined-basic2"
              fullWidth
              variant="outlined"
              label="Name"
              name="name"
              value={sellerDetails.name}
              required
              onChange={(t) => {
                setSellerDetails((pre) => ({
                  ...pre,
                  name: t.target.value,
                }));
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              id="outlined-basic2"
              fullWidth
              variant="outlined"
              label="Phone Number"
              name="phoneNumber"
              type="tel"
              value={sellerDetails.phoneNumber}
              required
              onChange={(t) => {
                setSellerDetails((pre) => ({
                  ...pre,
                  phoneNumber: t.target.value,
                }));
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button variant="contained" type="submit" style={{ marginTop: 10 }}>
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </SubCard>
    <UpdateSellerName />
    {canBulkCreate && <BulkSellerRegister />}
    </>
  );
}

export default SalonUserRegister;
